import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Block,
  Person,
  Computer,
  Add,
  Delete,
  Edit,
  Visibility,
  Warning,
  CheckCircle,
  Search,
  Refresh,
  Shield,
  Security,
  DeleteSweep,
  PowerSettingsNew,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import emergencyAPI from '../../services/emergencyAPI';
import type { EmergencyStatus } from '../../types/emergency';

interface BlockedUser {
  id: string;
  email: string;
  username?: string;
  blocked_at: string;
  reason: string;
  admin_note?: string;
}

interface BlockedIP {
  ip: string;
  blocked_at: string;
  reason: string;
  admin_note?: string;
  country?: string;
  city?: string;
}

const UserIPManagement: React.FC = () => {
  const [status, setStatus] = useState<EmergencyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  
  // Form states
  const [newUserEmail, setNewUserEmail] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [newIP, setNewIP] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [duration, setDuration] = useState<number>(60);
  
  // User list states
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  
  // Mock data for demonstration - replace with real API calls
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([
    {
      ip: '192.168.0.55',
      blocked_at: new Date().toISOString(),
      reason: 'Suspicious activity detected',
      admin_note: 'Multiple failed login attempts',
      country: 'Unknown',
      city: 'Unknown'
    }
  ]);

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (dialogOpen === 'block-user') {
      fetchUsers();
    }
  }, [dialogOpen]);

  const fetchUsers = async (search?: string) => {
    setUsersLoading(true);
    try {
      const response = await emergencyAPI.getUsers({
        page: 1,
        limit: 50,
        search: search || undefined
      });
      setUsers(response.users || []);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await emergencyAPI.getStatus();
      setStatus(response || null);
      // Here you would also fetch blocked users and IPs from API
      // await fetchBlockedUsers();
      // await fetchBlockedIPs();
    } catch (error: any) {
      console.error('Failed to fetch emergency status:', error);
      toast.error('Failed to load emergency status');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async () => {
    if (selectedUserIds.length === 0 || !blockReason.trim()) {
      toast.error('Please select user(s) and provide a reason');
      return;
    }

    setActionLoading('block-user');
    try {
      // Block each selected user
      const results = [];
      for (const userId of selectedUserIds) {
        const request = {
          user_id: userId,
          reason: blockReason.trim(),
          admin_note: adminNote.trim() || undefined
        };

        const response = await emergencyAPI.blockUser(request);
        results.push(response);
        
        // Add to local state (replace with API fetch)
        const selectedUser = users.find(u => u.id === userId);
        if (selectedUser) {
          const newBlockedUser: BlockedUser = {
            id: userId,
            email: selectedUser.email,
            username: selectedUser.username,
            blocked_at: new Date().toISOString(),
            reason: blockReason.trim(),
            admin_note: adminNote.trim() || undefined
          };
          setBlockedUsers(prev => [...prev, newBlockedUser]);
        }
      }
      
      toast.success(`${selectedUserIds.length} user(s) blocked successfully`);
      
      setDialogOpen(null);
      resetForm();
      fetchStatus();
    } catch (error: any) {
      console.error('Failed to block user:', error);
      toast.error(`Failed to block user: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlockIP = async () => {
    if (!newIP.trim() || !blockReason.trim()) {
      toast.error('Please provide IP address and reason');
      return;
    }

    setActionLoading('block-ip');
    try {
      const request = {
        ip_address: newIP.trim(),
        reason: blockReason.trim(),
        duration_minutes: duration,
        admin_note: adminNote.trim() || undefined
      };

      const response = await emergencyAPI.blockIP(request);
      toast.success(response.message || 'IP blocked successfully');
      
      // Add to local state (replace with API fetch)
      const newBlockedIP: BlockedIP = {
        ip: newIP.trim(),
        blocked_at: new Date().toISOString(),
        reason: blockReason.trim(),
        admin_note: adminNote.trim() || undefined,
        country: 'Unknown',
        city: 'Unknown'
      };
      setBlockedIPs(prev => [...prev, newBlockedIP]);
      
      setDialogOpen(null);
      resetForm();
      fetchStatus();
    } catch (error: any) {
      console.error('Failed to block IP:', error);
      toast.error(`Failed to block IP: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblockUser = async (userId: string) => {
    setActionLoading(`unblock-user-${userId}`);
    try {
      const request = {
        reason: 'Manual unblock by admin',
        admin_note: 'Unblocked via admin interface'
      };

      const response = await emergencyAPI.unblockUser(userId, request);
      toast.success(response.message || 'User unblocked successfully');
      
      // Remove from local state (replace with API fetch)
      setBlockedUsers(prev => prev.filter(user => user.id !== userId));
      
      fetchStatus();
    } catch (error: any) {
      console.error('Failed to unblock user:', error);
      toast.error(`Failed to unblock user: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblockIP = async (ip: string) => {
    setActionLoading(`unblock-ip-${ip}`);
    try {
      const request = {
        reason: 'Manual unblock by admin',
        admin_note: 'Unblocked via admin interface'
      };

      const response = await emergencyAPI.unblockIP(ip, request);
      toast.success(response.message || 'IP unblocked successfully');
      
      // Remove from local state (replace with API fetch)
      setBlockedIPs(prev => prev.filter(item => item.ip !== ip));
      
      fetchStatus();
    } catch (error: any) {
      console.error('Failed to unblock IP:', error);
      toast.error(`Failed to unblock IP: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const resetForm = () => {
    setNewUserEmail('');
    setSelectedUserIds([]);
    setNewIP('');
    setBlockReason('');
    setAdminNote('');
    setDuration(60);
    setUserSearch('');
    setUsers([]);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const isValidIP = (ip: string) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Shield sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">User & IP Management</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchStatus}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Status Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Blocked Users</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {status?.blocked_users_count || blockedUsers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Currently blocked from accessing the system
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Computer sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Blocked IPs</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {status?.blocked_ips_count || blockedIPs.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                IP addresses blocked from system access
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab 
            label={`Blocked Users (${blockedUsers.length})`} 
            icon={<Person />} 
            iconPosition="start" 
          />
          <Tab 
            label={`Blocked IPs (${blockedIPs.length})`} 
            icon={<Computer />} 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Paper sx={{ p: 3 }}>
          {/* Users Tab Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">👤 Blocked Users Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialogOpen('block-user')}
              color="error"
            >
              Block User
            </Button>
          </Box>

          {/* Users Table */}
          {blockedUsers.length === 0 ? (
            <Alert severity="info" sx={{ textAlign: 'center' }}>
              <Typography>No users are currently blocked</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Use the "Block User" button to add users to the block list
              </Typography>
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Blocked At</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Admin Note</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.username || '-'}</TableCell>
                      <TableCell>{formatDateTime(user.blocked_at)}</TableCell>
                      <TableCell>
                        <Tooltip title={user.reason}>
                          <Typography noWrap sx={{ maxWidth: 200 }}>
                            {user.reason}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {user.admin_note ? (
                          <Tooltip title={user.admin_note}>
                            <Typography noWrap sx={{ maxWidth: 150 }}>
                              {user.admin_note}
                            </Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Unblock User">
                          <IconButton
                            color="success"
                            onClick={() => handleUnblockUser(user.id)}
                            disabled={actionLoading === `unblock-user-${user.id}`}
                          >
                            {actionLoading === `unblock-user-${user.id}` ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PowerSettingsNew />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {currentTab === 1 && (
        <Paper sx={{ p: 3 }}>
          {/* IPs Tab Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">🌐 Blocked IPs Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialogOpen('block-ip')}
              color="error"
            >
              Block IP
            </Button>
          </Box>

          {/* IPs Table */}
          {blockedIPs.length === 0 ? (
            <Alert severity="info" sx={{ textAlign: 'center' }}>
              <Typography>No IP addresses are currently blocked</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Use the "Block IP" button to add IP addresses to the block list
              </Typography>
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Blocked At</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Admin Note</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockedIPs.map((ip) => (
                    <TableRow key={ip.ip}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {ip.ip}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {ip.city && ip.country ? `${ip.city}, ${ip.country}` : 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDateTime(ip.blocked_at)}</TableCell>
                      <TableCell>
                        <Tooltip title={ip.reason}>
                          <Typography noWrap sx={{ maxWidth: 200 }}>
                            {ip.reason}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {ip.admin_note ? (
                          <Tooltip title={ip.admin_note}>
                            <Typography noWrap sx={{ maxWidth: 150 }}>
                              {ip.admin_note}
                            </Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Unblock IP">
                          <IconButton
                            color="success"
                            onClick={() => handleUnblockIP(ip.ip)}
                            disabled={actionLoading === `unblock-ip-${ip.ip}`}
                          >
                            {actionLoading === `unblock-ip-${ip.ip}` ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PowerSettingsNew />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Block User Dialog */}
      <Dialog 
        open={dialogOpen === 'block-user'} 
        onClose={() => setDialogOpen(null)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Block User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This will prevent the selected user(s) from accessing the system. Please provide a clear reason.
          </Alert>
          
          {/* Search Users */}
          <TextField
            fullWidth
            label="Search Users"
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value);
              fetchUsers(e.target.value);
            }}
            sx={{ mb: 2 }}
            placeholder="Search by email or username..."
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          {/* User Selection */}
          <Box sx={{ mb: 2, maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                No users found
              </Box>
            ) : (
              <List dense>
                {users.map((user) => (
                  <ListItem 
                    key={user.id} 
                    button 
                    onClick={() => {
                      const isSelected = selectedUserIds.includes(user.id);
                      if (isSelected) {
                        setSelectedUserIds(prev => prev.filter(id => id !== user.id));
                      } else {
                        setSelectedUserIds(prev => [...prev, user.id]);
                      }
                    }}
                    sx={{
                      backgroundColor: selectedUserIds.includes(user.id) ? 'action.selected' : 'transparent'
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {user.email}
                          </Typography>
                          {user.is_superuser && (
                            <Chip label="Admin" size="small" color="warning" />
                          )}
                          {!user.is_active && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {user.username && `@${user.username} • `}
                          {user.full_name && `${user.full_name} • `}
                          Created: {new Date(user.created_at).toLocaleDateString()}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
          
          {selectedUserIds.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {selectedUserIds.length} user(s) selected for blocking
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Reason (Required)"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            placeholder="e.g., Suspicious activity, Policy violation, etc."
          />
          
          <TextField
            fullWidth
            label="Admin Note (Optional)"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="Additional notes for audit trail"
          />
          
          <TextField
            fullWidth
            type="number"
            label="Duration (minutes)"
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
            inputProps={{ min: 1, max: 1440 }}
            helperText="Set duration for automatic unblock (1-1440 minutes)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(null)} disabled={!!actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBlockUser}
            variant="contained"
            color="error"
            disabled={selectedUserIds.length === 0 || !blockReason.trim() || !!actionLoading}
            startIcon={actionLoading === 'block-user' ? <CircularProgress size={16} /> : <Block />}
          >
            {actionLoading === 'block-user' ? 'Blocking...' : `Block ${selectedUserIds.length > 0 ? selectedUserIds.length : ''} User${selectedUserIds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block IP Dialog */}
      <Dialog open={dialogOpen === 'block-ip'} onClose={() => setDialogOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Block IP Address</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This will prevent access from the specified IP address. Please provide a clear reason.
          </Alert>
          
          <TextField
            fullWidth
            label="IP Address (Required)"
            value={newIP}
            onChange={(e) => setNewIP(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="192.168.1.100"
            error={newIP.length > 0 && !isValidIP(newIP)}
            helperText={newIP.length > 0 && !isValidIP(newIP) ? 'Invalid IP address format' : ''}
          />
          
          <TextField
            fullWidth
            label="Reason (Required)"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            placeholder="e.g., Suspicious activity, Brute force attack, etc."
          />
          
          <TextField
            fullWidth
            label="Admin Note (Optional)"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="Additional notes for audit trail"
          />
          
          <TextField
            fullWidth
            type="number"
            label="Duration (minutes)"
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
            inputProps={{ min: 1, max: 1440 }}
            helperText="Set duration for automatic unblock (1-1440 minutes)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(null)} disabled={!!actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBlockIP}
            variant="contained"
            color="error"
            disabled={!newIP.trim() || !blockReason.trim() || !isValidIP(newIP) || !!actionLoading}
            startIcon={actionLoading === 'block-ip' ? <CircularProgress size={16} /> : <Block />}
          >
            {actionLoading === 'block-ip' ? 'Blocking...' : 'Block IP'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserIPManagement; 